import React from 'react'
import {
    Dialog,
    DialogContent,
    IconButton,
    Chip,
    Typography,
    Box,
    Slide,
    Divider,
} from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import CloseIcon from '@mui/icons-material/Close'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import api from '@/services/api'
import toast from 'react-hot-toast'


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />
})

const TEAL = '#5BC0BE'
const TEAL_DARK = '#3a9e9c'
const TEAL_LIGHT = '#e6f7f7'

interface TablePopupProps {
    open: boolean
    handleClose: () => void
    data: any[]
    title: string
    onEnroll?: (item: any) => void
}

const TablePopup = ({ open, handleClose, data, title, onEnroll }: TablePopupProps) => {
    const handleEnroll = async (item: any) => {

        try {
            const payload = {
                payerId: item.payer_id.payerid,
                providerId: item.provider_id,
            }
            // I WANT TO CALL API AND API GIVE ME URL AND I WNAT TO OPEN THIS URL
            const response = await api.post('/insurance-claims/enroll-provider-payer', payload)
            window.open(response.data.enrollmentUrl, '_blank')



        } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred'
            toast.error(errorMessage)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(91,192,190,0.25)',
                },
            }}
        >
            {/* ── Header ── */}
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${TEAL_DARK} 0%, ${TEAL} 100%)`,
                    px: 3,
                    pt: 3,
                    pb: 2.5,
                }}
            >
                <Box display="flex" alignItems="center" gap={1.5}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.18)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            flexShrink: 0,
                        }}
                    >
                        <HealthAndSafetyIcon sx={{ color: '#fff', fontSize: 22 }} />
                    </Box>

                    <Box flex={1}>
                        <Typography
                            sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}
                        >
                            {title || 'Payer Information'}
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem' }}>
                            {data?.length ?? 0} record{data?.length !== 1 ? 's' : ''} found
                        </Typography>
                    </Box>

                    <IconButton
                        onClick={handleClose}
                        size="small"
                        sx={{
                            color: 'rgba(255,255,255,0.85)',
                            background: 'rgba(255,255,255,0.12)',
                            '&:hover': { background: 'rgba(255,255,255,0.22)', color: '#fff' },
                            transition: 'all 0.2s',
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            </Box>

            {/* ── Content ── */}
            <DialogContent sx={{ p: 0, bgcolor: '#f8fafa' }}>
                {/* Column headers */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr 1fr',
                        px: 3,
                        py: 1.5,
                        bgcolor: TEAL_LIGHT,
                        borderBottom: `1.5px solid ${TEAL}22`,
                    }}
                >
                    {['Payer Name', 'Is Initiated', 'Enrollment Status', 'Action'].map((col) => (
                        <Typography
                            key={col}
                            sx={{
                                fontWeight: 700,
                                color: TEAL_DARK,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                                fontSize: '0.68rem',
                            }}
                        >
                            {col}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                    {data && data.length > 0 ? (
                        data.map((item: any, index: number) => {

                            const isActive = item?.enrollment_initiated
                            const isEnrolled = item?.enrollment_status === 'Enrolled'

                            return (
                                <React.Fragment key={item.id || index}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '2fr 1fr 1fr 1fr',
                                            px: 3,
                                            py: 1.75,
                                            alignItems: 'center',
                                            bgcolor: index % 2 === 0 ? '#fff' : '#f5fafa',
                                            transition: 'background 0.15s',
                                            '&:hover': { bgcolor: `${TEAL}12` },
                                        }}
                                    >
                                        {/* Payer Name */}
                                        <Box display="flex" alignItems="center" gap={1.2}>
                                            <Box
                                                sx={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '8px',
                                                    background: `linear-gradient(135deg, ${TEAL_LIGHT}, ${TEAL}33)`,
                                                    border: `1px solid ${TEAL}44`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}
                                            >
                                                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: TEAL_DARK }}>
                                                    {(item?.payer_id?.payer_name || 'P').charAt(0).toUpperCase()}
                                                </Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#1e293b' }}>
                                                {item?.payer_id?.payer_name || '—'}
                                            </Typography>
                                        </Box>

                                        {/*enrollment_initiated */}

                                        <Box>
                                            <Typography sx={{ marginLeft: "20px" }}>{item?.enrollment_initiated ? 'Yes' : 'No'}</Typography>
                                        </Box>

                                        {/* Status */}
                                        <Box>
                                            <Chip
                                                icon={
                                                    <FiberManualRecordIcon
                                                        sx={{
                                                            fontSize: '8px !important',
                                                            color: `${isEnrolled ? '#16a34a' : '#dc2626'} !important`,
                                                        }}
                                                    />
                                                }
                                                label={item?.enrollment_status || 'Unknown'}
                                                size="small"
                                                sx={{
                                                    height: 24,
                                                    fontSize: '0.72rem',
                                                    fontWeight: 600,
                                                    bgcolor: isEnrolled ? '#dcfce7' : '#fee2e2',
                                                    color: isEnrolled ? '#15803d' : '#b91c1c',
                                                    border: `1px solid ${isEnrolled ? '#bbf7d0' : '#fecaca'}`,
                                                    borderRadius: '6px',
                                                    '& .MuiChip-label': { px: 1 },
                                                    '& .MuiChip-icon': { ml: '6px' },
                                                }}
                                            />
                                        </Box>

                                        {/* Action */}
                                        {item?.enrollment_status.toLowerCase() === 'pending' && (
                                            <Box>
                                                <Box
                                                    component="button"
                                                    onClick={() => handleEnroll(item)}
                                                    sx={{
                                                        px: 2,
                                                        py: 0.6,
                                                        borderRadius: '7px',
                                                        border: `1.5px solid ${TEAL}`,
                                                        bgcolor: TEAL,
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                        fontSize: '0.75rem',
                                                        cursor: 'pointer',
                                                        letterSpacing: '0.02em',
                                                        transition: 'all 0.2s',
                                                        '&:hover': {
                                                            bgcolor: TEAL_DARK,
                                                            borderColor: TEAL_DARK,
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 4px 12px ${TEAL}55`,
                                                        },
                                                        '&:active': { transform: 'translateY(0)' },
                                                    }}
                                                >
                                                    Enroll Now
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                    {index < data.length - 1 && (
                                        <Divider sx={{ borderColor: `${TEAL}18` }} />
                                    )}
                                </React.Fragment>
                            )
                        })
                    ) : (
                        <Box
                            sx={{
                                py: 6,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: '14px',
                                    bgcolor: TEAL_LIGHT,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <HealthAndSafetyIcon sx={{ color: `${TEAL}88`, fontSize: 28 }} />
                            </Box>
                            <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                No payer data available
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* ── Footer ── */}
                <Box
                    sx={{
                        px: 3,
                        py: 2,
                        bgcolor: '#f8fafa',
                        borderTop: `1px solid ${TEAL}22`,
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Box
                        component="button"
                        onClick={handleClose}
                        sx={{
                            px: 3,
                            py: 1,
                            borderRadius: '8px',
                            border: `1.5px solid ${TEAL}`,
                            bgcolor: '#fff',
                            color: TEAL_DARK,
                            fontWeight: 600,
                            fontSize: '0.82rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                                bgcolor: TEAL_LIGHT,
                                color: TEAL_DARK,
                            },
                        }}
                    >
                        Close
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    )
}

export default TablePopup